import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Mail, Phone, ChevronRight, ChevronDown, ExternalLink, Package, Users, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function absoluteImg(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminRestock() {
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("waiting"); // waiting | notified | all
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [er, pr] = await Promise.all([
        api.get("/admin/restock-enquiries"),
        api.get("/products"),
      ]);
      setEnquiries(er.data || []);
      setProducts(pr.data || []);
    } catch (err) {
      toast.error("FAILED TO LOAD", { description: err?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const productMap = useMemo(() => {
    const m = {};
    for (const p of products) m[p.id] = p;
    return m;
  }, [products]);

  // Group enquiries by product
  const grouped = useMemo(() => {
    const g = {};
    for (const e of enquiries) {
      if (filter === "waiting" && e.notified) continue;
      if (filter === "notified" && !e.notified) continue;
      if (!g[e.product_id]) g[e.product_id] = { product_id: e.product_id, product_name: e.product_name, product_slug: e.product_slug, enquiries: [] };
      g[e.product_id].enquiries.push(e);
    }
    const arr = Object.values(g);
    // Sort by waiting count desc, then newest
    arr.forEach((row) => row.enquiries.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    arr.sort((a, b) => b.enquiries.length - a.enquiries.length);
    return arr;
  }, [enquiries, filter]);

  const totalWaiting = enquiries.filter((e) => !e.notified).length;
  const totalNotified = enquiries.filter((e) => e.notified).length;

  const restockToggle = expandedId;

  return (
    <AdminLayout title="Restock Enquiries">
      {/* Header stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="TOTAL ENQUIRIES" value={enquiries.length} icon={Users} accent />
        <StatCard label="AWAITING" value={totalWaiting} icon={BellRing} accent={filter === "waiting"} />
        <StatCard label="NOTIFIED" value={totalNotified} icon={Mail} />
        <StatCard label="SKUS ON WAITLIST" value={grouped.length} icon={Package} />
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex border border-[#27272A]" data-testid="restock-filter-tabs">
          {[
            { id: "waiting", label: "Waiting" },
            { id: "notified", label: "Notified" },
            { id: "all", label: "All" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`label-mono px-4 py-2 transition-colors ${filter === t.id ? "bg-[#CFF048] text-black" : "text-white hover:bg-[#111]"}`}
              data-testid={`restock-filter-${t.id}`}
            >{t.label}</button>
          ))}
        </div>
        <div className="flex-1" />
        <button onClick={load} className="btn-outline flex items-center gap-2" data-testid="restock-refresh-btn">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> REFRESH
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#71717A] label-mono flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> LOADING…
        </div>
      ) : grouped.length === 0 ? (
        <div className="border border-dashed border-[#27272A] py-20 text-center" data-testid="restock-empty">
          <BellRing size={38} className="mx-auto text-[#71717A]" />
          <p className="label-mono text-[#71717A] mt-4">// NO {filter.toUpperCase()} ENQUIRIES</p>
          <p className="text-sm text-[#A1A1AA] mt-2">
            When shoppers hit a Sold-Out product, their emails will show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="restock-groups">
          {grouped.map((row) => {
            const product = productMap[row.product_id];
            const waiting = row.enquiries.filter((e) => !e.notified).length;
            const notified = row.enquiries.filter((e) => e.notified).length;
            const stock = product?.stock ?? "—";
            const isSold = stock === 0 || stock === "—";
            const expanded = expandedId === row.product_id;

            return (
              <div key={row.product_id} className="border border-[#27272A] bg-[#0A0A0A]" data-testid={`restock-row-${row.product_slug}`}>
                <button
                  onClick={() => setExpandedId(expanded ? null : row.product_id)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-[#111] transition-colors text-left"
                  data-testid={`restock-toggle-${row.product_slug}`}
                >
                  {product ? (
                    <img src={absoluteImg(product.images?.[0])} alt="" className="w-14 h-14 object-cover border border-[#27272A]" />
                  ) : (
                    <div className="w-14 h-14 border border-[#27272A] bg-black flex items-center justify-center">
                      <Package size={20} className="text-[#71717A]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold truncate">{row.product_name || "Deleted product"}</p>
                    <p className="label-mono text-[#71717A] mt-0.5">
                      // {row.enquiries.length} ENQUIRIES · STOCK: <span className={isSold ? "text-red-400" : "text-[#CFF048]"}>{stock}</span>
                      {product?.is_published === false ? <span className="text-[#71717A]"> · HIDDEN</span> : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {waiting > 0 && (
                      <span className="bg-[#CFF048] text-black label-mono px-2 py-1 text-[10px]" data-testid={`restock-waiting-${row.product_slug}`}>
                        {waiting} WAITING
                      </span>
                    )}
                    {notified > 0 && (
                      <span className="border border-[#27272A] text-[#71717A] label-mono px-2 py-1 text-[10px]">
                        {notified} SENT
                      </span>
                    )}
                  </div>
                  {expanded ? <ChevronDown size={18} className="text-[#71717A]" /> : <ChevronRight size={18} className="text-[#71717A]" />}
                </button>

                {expanded && (
                  <div className="border-t border-[#27272A]" data-testid={`restock-expanded-${row.product_slug}`}>
                    {product && (
                      <div className="p-4 flex items-center justify-between gap-3 bg-black border-b border-[#27272A] flex-wrap">
                        <p className="label-mono text-[#71717A]">
                          // Restock this SKU (stock &gt; 0 + PUBLISHED) to auto-email every waitlisted member.
                        </p>
                        <Link
                          to={`/admin/products`}
                          className="btn-neon flex items-center gap-1"
                          data-testid={`restock-openproduct-${row.product_slug}`}
                        >
                          <Package size={14} /> RESTOCK SKU
                        </Link>
                        <a
                          href={`/product/${row.product_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-outline flex items-center gap-1"
                          data-testid={`restock-viewproduct-${row.product_slug}`}
                        >
                          <ExternalLink size={13} /> VIEW PDP
                        </a>
                      </div>
                    )}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-black">
                          <tr className="label-mono text-[#71717A]">
                            <th className="text-left p-3">EMAIL</th>
                            <th className="text-left p-3">NAME</th>
                            <th className="text-left p-3">PHONE</th>
                            <th className="text-left p-3">SIZE</th>
                            <th className="text-left p-3">SUBMITTED</th>
                            <th className="text-left p-3">STATUS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.enquiries.map((e) => (
                            <tr key={e.id} className="border-t border-[#27272A] hover:bg-[#111]" data-testid={`restock-enquiry-${e.id}`}>
                              <td className="p-3">
                                <a href={`mailto:${e.email}`} className="text-white hover:text-[#CFF048] font-mono-cult text-[13px] flex items-center gap-1.5">
                                  <Mail size={11} /> {e.email}
                                </a>
                              </td>
                              <td className="p-3 text-[#A1A1AA]">{e.name || "—"}</td>
                              <td className="p-3">
                                {e.phone ? (
                                  <a href={`tel:${e.phone}`} className="text-[#A1A1AA] hover:text-[#CFF048] font-mono-cult flex items-center gap-1.5">
                                    <Phone size={11} /> {e.phone}
                                  </a>
                                ) : <span className="text-[#71717A]">—</span>}
                              </td>
                              <td className="p-3 font-mono-cult text-[#CFF048]">{e.size || "—"}</td>
                              <td className="p-3 label-mono text-[#71717A] text-[11px]">{fmtDate(e.created_at)}</td>
                              <td className="p-3">
                                {e.notified ? (
                                  <span className="label-mono text-[#71717A] text-[10px]">
                                    ✓ SENT · {fmtDate(e.notified_at)}
                                  </span>
                                ) : (
                                  <span className="label-mono text-[#CFF048] text-[10px]">● WAITING</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Hidden helper for lint keep */}
      <span className="hidden">{restockToggle}</span>
    </AdminLayout>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`border p-4 ${accent ? "border-[#CFF048] bg-[#CFF048]/5" : "border-[#27272A] bg-[#0A0A0A]"}`}>
      <div className="flex items-center justify-between">
        <p className="label-mono text-[#71717A]">{label}</p>
        <Icon size={14} className={accent ? "text-[#CFF048]" : "text-[#71717A]"} />
      </div>
      <p className={`font-display font-black text-3xl mt-2 ${accent ? "text-[#CFF048]" : "text-white"}`}>{value}</p>
    </div>
  );
}
